import {
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

import { Box, Button, CircularProgress } from '@mui/material';
import ModalComponent from '../../ModalComponent';
import { VideoTypeValue } from '../../../utils/video';
import config from '../../../config';
import InputVideo from '../../Form/InputVideo';
import VideoSelectionList from '../../../pages/Master/BlockVideosManagement/VideoSelectionList';
import { VideoContext, VideoInterface } from '../../../contexts/VideoContext';

interface VideoModalProps {
  currentFilename: string
  editHandler: (video: VideoInterface) => void
}

function VideoModal({ editHandler, currentFilename }: VideoModalProps) {
  const customNavTabs = 'nav navTabs';
  const customTabContent = 'tab-content tabContent';

  const {
    videos,
    createVideo,
  } = useContext(VideoContext);
  const [show, setShow] = useState(false);
  const [currentTab] = useState(VideoTypeValue.SYSTEM_VIDEO);
  const [displayVideos, setDisplayVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState<VideoInterface>(videos.find((video) => video.file_name === currentFilename));

  const [isLoading, setIsLoading] = useState(false);

  // errors
  const [videoMaxSizeError, setVideoMaxSizeError] = useState<string | null>(null);

  const handleImageUpload = useCallback((video: File) => {
    setIsLoading(true);

    createVideo(
      video,
      currentTab,
      () => {
        setIsLoading(false);
      },
      () => { setShow(true); },
    );
  }, [createVideo, currentTab]);

  const handleSave = useCallback((video: VideoInterface) => {
    if (video === null) return;

    editHandler(video);
    setShow(false);
  }, [editHandler]);

  useEffect(() => {
    setDisplayVideos(videos.filter((video) => video.type === currentTab));
  }, [currentTab, videos]);

  useEffect(() => {
    setSelectedVideo(videos.find((video) => video.file_name === currentFilename));
  }, [currentFilename, videos]);

  return (
    <>
      <Button
        onClick={(event) => {
          setShow(true);
          event.stopPropagation();
        }}
        variant="contained"
        component="label"
        sx={{
          backgroundColor: '#5555558A',
        }}
      >
        画像を選択する
      </Button>

      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <ModalComponent
          size="xl"
          title="動画選択"
          confirmButtonText="選択する"
          onConfirm={() => {
            handleSave(selectedVideo);
            setSelectedVideo(null);
            setVideoMaxSizeError(null);
          }}
          show={show}
          // disabled={selectedImage === null}
          closeModal={() => {
            setVideoMaxSizeError(null);
            setShow(false);
          }}
          centered
          animation={false}
          bodyClassName="m-0 p-0"
          backdropClassName="z-index-2000"
          style={{ zIndex: 2000 }}
        >
          <div className="tab modalTabs">
            <ul className={customNavTabs} role="tablist" style={{ overflow: 'auto' }}>
              <li><a className="active" href="#tabImage_1" data-bs-toggle="tab" role="tab">システム動画</a></li>
            </ul>
            <div className={customTabContent}>
              <div id="tabImage_1" className="tab-pane tabPane active">
                <div
                  style={{ display: currentTab === VideoTypeValue.MY_VIDEO ? 'block' : 'none' }}
                  className="imageUpload"
                >
                  <Box>
                    <Button
                      className="btn btn-primary"
                      variant="contained"
                      component="label"
                      disabled={isLoading}
                      sx={{
                        position: 'relative',
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={15} />
                      ) : (
                        <>
                          <InputVideo
                            id="id"
                            type={currentTab}
                            isDisabled={false}
                            fileHandler={(file) => handleImageUpload(file)}
                            onMaxVideoSizeExceeded={(message: any) => {
                              setVideoMaxSizeError(message);
                            }}
                          />
                          動画をアップロードする ...
                        </>
                      )}
                    </Button>
                    <span style={{ marginLeft: 5 }}>{`※動画最大ファイルサイズは${config.video.maxVideoSizeInMb}MBです。`}</span>
                    {videoMaxSizeError && (
                      <div className="text-danger mt-2">
                        {videoMaxSizeError}
                      </div>
                    )}
                  </Box>
                </div>

                <VideoSelectionList
                  videos={displayVideos}
                  selectedId={selectedVideo?.id}
                  handleSelected={(id) => {
                    setSelectedVideo(videos.find((video) => video.id === id));
                  }}
                />
              </div>
            </div>
          </div>
        </ModalComponent>
      </div>
    </>
  );
}

export default VideoModal;
